import { InfoCircleIcon } from "@expert/common-ui/assets/InfoCircleIcon";
import { Card, Image, Stack, Text } from "@mantine/core";
import { memo } from "react";
import { useProductOfferStore } from "@/sales-tools";
import { useSessionId } from "@/sdk";
import type { SalesDisclosureAttributes } from "@/sdk/agent/chat/types";
import classes from "./sales-disclosure-card.module.css";

interface SalesDisclosureCardProps {
    attributes: SalesDisclosureAttributes;
}

/** Displays a sales disclosure preview intended for use in the message list */
export const SalesDisclosureCard = memo(({ attributes }: SalesDisclosureCardProps) => {
    const { amountPerMonth, programDisplayName, programDescription, programSku, callToAction } = attributes;
    const sessionId = useSessionId();
    const productImageUrl = useProductOfferStore.getState().getProductInfo(sessionId, programSku)?.logo;

    return (
        <Stack className={classes.container}>
            <Card className={classes.card} shadow="sm" radius="md" withBorder>
                {productImageUrl && (
                    <Card.Section className={classes.imageSection}>
                        <Image
                            src={productImageUrl}
                            alt={programDisplayName}
                            className={classes.productImage}
                            fit="contain"
                        />
                    </Card.Section>
                )}
                <Text fw={700} size="xs" c="dark.9">
                    {programDisplayName}
                </Text>
                <Text size="xxs" c="dark.4">
                    {programDescription}
                </Text>
                {amountPerMonth && (
                    <Stack gap={0} m={0} mt="xs">
                        <Text fw={700} size="md" span c="dark.9">
                            ${amountPerMonth}/mo
                        </Text>
                        <Text size="xxs" c="dark.4">
                            plus taxes
                        </Text>
                    </Stack>
                )}
                <Text className={classes.fakeActionButton}>{callToAction}</Text>
            </Card>
            <Text className={classes.infoText}>
                <InfoCircleIcon />
                {`Here's what the customer sees.`}
            </Text>
        </Stack>
    );
});

SalesDisclosureCard.displayName = "SalesDisclosureCard";
